import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import { getPriceDarkBgColor, getPriceLightBgColor } from "../../utils/HelperFunctions";
import battle1 from "../../assets/images/Battles/battle1.png";
import battle2 from "../../assets/images/Battles/battle2.png";
import "./battleUser.css";
import {ReactComponent as SwordIcon} from "../../assets/icons/swords.svg"
import { ReactComponent as CrazyZap } from "../../assets/icons/crazy_zap.svg";

const BattleFooter = ({ player1, player2, totalBoxes, currentBox }) => {
  const user = useSelector((state) => state?.auth?.user);
  const battleFromRedux = useSelector((state) => state.battle.currentBattle);
  const battleStatus = battleFromRedux?.status;
  const isFinished = battleStatus === "finished" || battleStatus === "ended";
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);

  const mergedPlayer1 = player1 || battleFromRedux?.user_1;
  const mergedPlayer2 = player2 || battleFromRedux?.user_2;

  const getValue = (p) => parseFloat(p?.total_unboxed_value || p?.total_amount || 0);
  const actualValue1 = getValue(mergedPlayer1);
  const actualValue2 = getValue(mergedPlayer2);

  const [displayValue1, setDisplayValue1] = useState(actualValue1);
  const [displayValue2, setDisplayValue2] = useState(actualValue2);

  useEffect(() => {
    if (displayValue1 === actualValue1) return;
    const startValue = displayValue1;
    const difference = actualValue1 - startValue;
    const duration = 2000;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * (2 - progress);
      setDisplayValue1(startValue + difference * eased);
      if (progress < 1) requestAnimationFrame(animate);
      else setDisplayValue1(actualValue1);
    };
    requestAnimationFrame(animate);
  }, [actualValue1]);

  useEffect(() => {
    if (displayValue2 === actualValue2) return;
    const startValue = displayValue2;
    const difference = actualValue2 - startValue;
    const duration = 2000;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * (2 - progress);
      setDisplayValue2(startValue + difference * eased);
      if (progress < 1) requestAnimationFrame(animate);
      else setDisplayValue2(actualValue2);
    };
    requestAnimationFrame(animate);
  }, [actualValue2]);

  const isWinner1 = isFinished && actualValue1 > actualValue2;
  const isWinner2 = isFinished && actualValue2 > actualValue1;

  const getDisplayItems = (player) => {
    const allWon = player?.total_products || [];
    const won = isFinished ? allWon : allWon.slice(0, currentBox);
    const items = [...won.filter(Boolean)];
    if (!isFinished) {
      const remaining = totalBoxes - items.length;
      for (let i = 0; i < remaining; i++) items.push(null);
    }
    return items;
  };

  const items1 = getDisplayItems(mergedPlayer1);
  const items2 = getDisplayItems(mergedPlayer2);

  const getAvatar = (player, isCreator) =>
    player?.user?.userdetails?.avatar ||
    player?.userdetails?.avatar ||
    player?.user?.avatar ||
    (isCreator ? battle1 : battle2);

  const getUsername = (player, isCreator) => {
    if (!player) return "Waiting…";
    if (player?.user?.id === user?.id) return "You";
    return (
      player?.user?.userdetails?.username ||
      player?.userdetails?.userdetails?.username ||
      player?.user?.username ||
      player?.username ||
      (isCreator ? "Player 1" : "Player 2")
    );
  };

  /* ── Card renderer ── */
  const renderCard = (item, key, roundNum) =>
    item ? (
      <div
        key={key}
        style={{
          background: isDarkMode
            ? getPriceDarkBgColor(item.price)
            : getPriceLightBgColor(item.price),
        }}
        className="bf-card"
      >
        {/* Radial glow overlay */}
        {isDarkMode && (
          <div
            className="bf-card-glow"
            style={{
              background:getPriceDarkBgColor(item.price) 
            }}
          />
        )}
        <span className="bf-card-num">{roundNum}</span>
        <div className="bf-card-img">
          <img src={item.image} alt={item.name} />
        </div>
        <div className="bf-card-footer">
          <div className="bf-card-price">${item.price.toFixed(2)}</div>
        </div>
      </div>
    ) : (
      <div key={key} className="bf-card bf-card-empty">
        <span className="bf-card-num">{roundNum}</span>
        <span className="bf-empty-text">Waiting...</span>
      </div>
    );

  /* ── Player header renderer ── */
  const renderHeader = (player, isCreator, isWinner) => (
    <div className={`bf-player-header ${isWinner ? "bf-winner" : ""} ${isDarkMode ? "" : "bf-header-light"}`}>
      <div className="bf-av-wrap">
        <img
          src={getAvatar(player, isCreator)}
          alt={isCreator ? "Player 1" : "Player 2"}
        />
      </div>
      <div className="bf-meta">
        <h6>{getUsername(player, isCreator)}</h6>
        <span className={`bf-val-badge ${isWinner ? "bf-val-blue" : "bf-val-green"}`}>
          ${(isCreator ? displayValue1 : displayValue2).toFixed(2)}
        </span>
      </div>
    </div>
  );

  /* ── Mobile interleaved cards: P1 left | P2 right per round ── */
  const renderMobileCards = () => {
    const maxLen = Math.max(items1.length, items2.length);
    const cells = [];
    for (let i = 0; i < maxLen; i++) {
      // P1 card for this round (left column)
      cells.push(
        items1[i] !== undefined
          ? renderCard(items1[i], `mob-p1-${i}`, i + 1)
          : <div key={`mob-p1-empty-${i}`} className="bf-card bf-card-empty">
              <span className="bf-card-num">{i + 1}</span>
              <span className="bf-empty-text">Waiting...</span>
            </div>
      );
      // P2 card for this round (right column)
      cells.push(
        items2[i] !== undefined
          ? renderCard(items2[i], `mob-p2-${i}`, i + 1)
          : <div key={`mob-p2-empty-${i}`} className="bf-card bf-card-empty">
              <span className="bf-card-num">{i + 1}</span>
              <span className="bf-empty-text">Waiting...</span>
            </div>
      );
    }
    return cells;
  };

  return (
    <Container className="px-3 py-4">

      {/* ════════════════════════════════
          DESKTOP LAYOUT (hidden on mobile via CSS)
          ════════════════════════════════ */}
      <div className="bf-root">
        {/* ── Player 1 column ── */}
        <div className="bf-player-col">
          {renderHeader(mergedPlayer1, true, isWinner1)}
          <div className="bf-cards-grid">
            {items1.map((item, i) => renderCard(item, `p1-${i}`, i + 1))}
          </div>
        </div>

        {/* ── Sword separator ── */}
        <div className="bf-sep">
          {/* <SwordIcon /> */}
          <CrazyZap />
        </div>

        {/* ── Player 2 column ── */}
        <div className="bf-player-col">
          {renderHeader(mergedPlayer2, false, isWinner2)}
          <div className="bf-cards-grid">
            {items2.map((item, i) => renderCard(item, `p2-${i}`, i + 1))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          MOBILE LAYOUT (hidden on desktop via CSS)
          Both headers side-by-side, cards interleaved 2-col grid
          ════════════════════════════════ */}
      <div className="bf-mobile-layout">
        {/* Both headers in a row */}
        <div className="bf-mobile-headers">
          {renderHeader(mergedPlayer1, true, isWinner1)}
          {renderHeader(mergedPlayer2, false, isWinner2)}
        </div>

        {/* Interleaved cards: [P1 round N] [P2 round N] per row */}
        <div className="bf-mobile-cards-grid">
          {renderMobileCards()}
        </div>
      </div>

    </Container>
  );
};

export default BattleFooter;